import { gql } from '@apollo/client';

import { MESSAGE_COMMENT_FRAGMENT } from './fragments';

/**
 * AI feature GraphQL operations. Kept in a dedicated file (instead of the
 * monolithic `mutations.ts` / `queries.ts`) so the AI surface is easy to
 * find and to delete if the feature is ever rolled back.
 *
 * Types like `AiGenerateResponseInput` come from `app-types/desk` after
 * codegen is re-run against the updated desk-service schema.
 */

// ─── Fragments ────────────────────────────────────────────────────────

export const AGENT_PREFERENCE_FRAGMENT = gql`
  fragment AgentPreferenceFragment on AgentPreferenceObject {
    id
    agentId
    agentLanguages
    defaultResponseLanguage
  }
`;

export const BRAND_KNOWLEDGE_FILE_FRAGMENT = gql`
  fragment BrandKnowledgeFileFragment on BrandKnowledgeFileObject {
    id
    brand
    filename
    mimeType
    size
    indexingStatus
    indexingError
    createdAt
  }
`;

// ─── Mutations ────────────────────────────────────────────────────────

/**
 * Returns `Boolean`. The backend persists the generated reply into
 * `ticket.draft` and emits a `desk:ticket-update` socket event; the
 * editor picks up the new draft through `DeskTicketFormProvider`.
 */
export const AI_GENERATE_RESPONSE_MUTATION = gql`
  mutation AI_GenerateResponse($input: AiGenerateResponseInput!) {
    AI_GenerateResponse(input: $input)
  }
`;

export const AI_TRANSLATE_MESSAGE_MUTATION = gql`
  mutation AI_TranslateMessage($input: AiTranslateMessageInput!) {
    AI_TranslateMessage(input: $input) {
      ...MessageCommentFragment
    }
  }
  ${MESSAGE_COMMENT_FRAGMENT}
`;

export const AGENT_PREFERENCE_UPDATE_MUTATION = gql`
  mutation AgentPreference_Update($input: AgentPreferenceUpdateInput!) {
    AgentPreference_Update(input: $input) {
      ...AgentPreferenceFragment
    }
  }
  ${AGENT_PREFERENCE_FRAGMENT}
`;

export const BRAND_KNOWLEDGE_FILE_REQUEST_UPLOAD_MUTATION = gql`
  mutation BrandKnowledgeFile_RequestUpload($input: BrandKnowledgeFileUploadRequestInput!) {
    BrandKnowledgeFile_RequestUpload(input: $input) {
      url
      fields
      s3Key
    }
  }
`;

export const BRAND_KNOWLEDGE_FILE_FINALIZE_MUTATION = gql`
  mutation BrandKnowledgeFile_Finalize($input: BrandKnowledgeFileFinalizeInput!) {
    BrandKnowledgeFile_Finalize(input: $input) {
      ...BrandKnowledgeFileFragment
    }
  }
  ${BRAND_KNOWLEDGE_FILE_FRAGMENT}
`;

export const BRAND_KNOWLEDGE_FILE_DELETE_MUTATION = gql`
  mutation BrandKnowledgeFile_Delete($id: String!) {
    BrandKnowledgeFile_Delete(id: $id)
  }
`;

// ─── Queries ──────────────────────────────────────────────────────────

export const AGENT_PREFERENCE_GET_QUERY = gql`
  query AgentPreference_Get {
    AgentPreference_Get {
      ...AgentPreferenceFragment
    }
  }
  ${AGENT_PREFERENCE_FRAGMENT}
`;

export const BRAND_KNOWLEDGE_FILE_LIST_QUERY = gql`
  query BrandKnowledgeFile_List($brandId: String!) {
    BrandKnowledgeFile_List(brandId: $brandId) {
      ...BrandKnowledgeFileFragment
    }
  }
  ${BRAND_KNOWLEDGE_FILE_FRAGMENT}
`;
